import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect, useMemo } from 'react';
import { TrendingUp, Clock, DollarSign, Target, Layers, RefreshCw, Loader2, Cpu, BarChart3, ChevronDown, ChevronUp, X, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const METRIC_ICONS = {
  retrievalRelevance: Target,
  attributionCoverage: Layers,
  costPerQuery: DollarSign,
  incrementalUpdate: RefreshCw,
  graphConstructionCost: DollarSign,
};

const STATIC_METRIC_KEYS = ['retrievalRelevance', 'attributionCoverage', 'graphConstructionCost'];
const STATIC_METRIC_VALUES = {
  retrievalRelevance: { doora: '56%', competitor: '47%' },
  attributionCoverage: { doora: '45.0', competitor: '0.6' },
  graphConstructionCost: { doora: '$35.55', competitor: '$584.50' },
};

const REBUILD_VALUE = { en: 'Full rebuild', id: 'Bangun ulang penuh' };

function parseMetricsFromAPI(data, language) {
  if (!data?.metrics?.accuracy) return null;
  const { accuracy, attribution, cost } = data.metrics;
  const clean = (v) => (v || '').replace(/\*\*/g, '').trim();

  const metrics = [];
  if (accuracy.fuserag && accuracy.graphrag) {
    metrics.push({ key: 'retrievalRelevance', doora: clean(accuracy.fuserag), competitor: clean(accuracy.graphrag) });
  }
  if (attribution.fuserag && attribution.graphrag) {
    metrics.push({ key: 'attributionCoverage', doora: clean(attribution.fuserag), competitor: clean(attribution.graphrag) });
  }
  if (cost.fuserag && cost.graphrag) {
    metrics.push({ key: 'costPerQuery', doora: cost.fuserag.per_query || '$0.025', competitor: cost.graphrag.per_query || '$0.0002' });
  }
  metrics.push({ key: 'incrementalUpdate', doora: '~25s/doc', competitor: REBUILD_VALUE[language] || REBUILD_VALUE.en });
  metrics.push({ key: 'graphConstructionCost', doora: '$35.55', competitor: '$584.50' });

  return metrics.length >= 3 ? metrics : null;
}

function buildUseCasesFromAPI(data) {
  if (!data?.metrics?.scenarios?.length) return null;
  const scenarioMap = {};
  for (const s of data.metrics.scenarios) {
    const sys = s.system.replace(/\*\*/g, '').trim();
    if (sys === 'FuseRAG') {
      scenarioMap[s.name] = { useCase: s.name, dooraScore: s.relevance, dooraAttr: s.attribution, takeaway: s.category };
    }
  }
  const cases = Object.values(scenarioMap);
  return cases.length >= 2 ? cases : null;
}

export const BenchmarkSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t, language } = useLanguage();
  const lang = t('benchmark');

  const [apiMetrics, setApiMetrics] = useState(null);
  const [apiUseCases, setApiUseCases] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const [showDetailed, setShowDetailed] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState(null);

  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || 'https://api.doora.id/api';
    fetch(`${API_BASE}/benchmark/latest`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.available) return;
        const parsedMetrics = parseMetricsFromAPI(data, language);
        if (parsedMetrics) { setApiMetrics(parsedMetrics); setIsLive(true); }
        const parsedCases = buildUseCasesFromAPI(data);
        if (parsedCases) setApiUseCases(parsedCases);
        if (data.generated_at) setGeneratedAt(data.generated_at);
      })
      .catch(() => {}); // fallback to static data
  }, [language]);

  const benchmarkMetrics = useMemo(() => {
    const source = apiMetrics || STATIC_METRIC_KEYS.map((key) => ({ key, ...STATIC_METRIC_VALUES[key] }));
    return source.map((m) => ({
      ...m,
      metric: lang.metricLabels[m.key]?.metric,
      insight: lang.metricLabels[m.key]?.insight,
      icon: METRIC_ICONS[m.key],
    }));
  }, [apiMetrics, lang]);

  const useCases = apiUseCases || lang.useCases;

  return (
    <section
      id="benchmark"
      data-testid="benchmark-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-zinc-950"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 block">
            {lang.eyebrow}
          </span>
          <h2
            data-testid="benchmark-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
          >
            {lang.headline} <span className="font-semibold gradient-text">{lang.headlineHighlight}</span>
          </h2>
          <p className="text-xs text-zinc-400 mb-3 tracking-wide uppercase font-medium">
            {lang.subheadline}
          </p>
        </motion.div>

        {/* Metrics Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-16"
        >
          {benchmarkMetrics.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                data-testid={`benchmark-metric-${index}`}
                className="glass-card rounded-2xl p-6 card-hover bg-zinc-950 border border-zinc-800"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-900/20 border border-blue-800 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-sm font-semibold text-zinc-200">{item.metric}</span>
                </div>

                <div className="flex items-end gap-4 mb-3">
                  <div>
                    <div className="text-3xl font-bold gradient-text">{item.doora}</div>
                    <div className="text-xs font-medium text-zinc-400 uppercase tracking-widest">{lang.dooraLabel}</div>
                  </div>
                  <div className="text-zinc-400 mb-1 font-medium italic">{lang.vsLabel}</div>
                  <div>
                    <div className="text-xl font-bold text-zinc-400">{item.competitor}</div>
                    <div className="text-xs font-medium text-zinc-400 uppercase tracking-widest">{lang.bestAlternative}</div>
                  </div>
                </div>

                <p className="text-xs text-zinc-400 font-medium">{item.insight}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Use Case Table & Research Quote */}
        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-6 text-zinc-100">
              {lang.useCaseSectionTitle}
            </h3>

            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left" data-testid="benchmark-usecase-table">
                <thead>
                  <tr className="border-b border-zinc-800 bg-zinc-900/50">
                    <th className="py-4 px-6 text-sm font-bold text-zinc-400 uppercase tracking-wider">
                      {lang.tableHeaders.useCase}
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-bold text-zinc-400 uppercase tracking-wider">
                      {lang.tableHeaders.relevance}
                    </th>
                    <th className="text-center py-4 px-4 text-sm font-bold text-zinc-400 uppercase tracking-wider">
                      {lang.tableHeaders.coverage}
                    </th>
                    <th className="py-4 px-6 text-sm font-bold text-zinc-400 uppercase tracking-wider hidden sm:table-cell">
                      {lang.tableHeaders.takeaway}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {useCases.map((row, index) => (
                    <motion.tr
                      key={row.key || row.useCase}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                      className="border-b border-zinc-800 hover:bg-blue-900/20 transition-colors last:border-0"
                      data-testid={`benchmark-usecase-row-${index}`}
                    >
                      <td className="py-4 px-6 text-sm font-bold text-zinc-100 w-[220px]">
                        <button
                          onClick={() => setSelectedUseCase(row)}
                          className="text-left text-blue-400 hover:text-blue-800 transition-colors flex items-center gap-1.5 group outline-none"
                        >
                          <span className="group-hover:underline underline-offset-4 decoration-blue-500/30">{row.useCase}</span>
                          <ExternalLink className="w-3 h-3 text-blue-400 group-hover:text-blue-400 opacity-60 group-hover:opacity-100" />
                        </button>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-lg font-extrabold text-blue-400">{row.dooraScore}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="text-sm font-bold text-zinc-700">{row.dooraAttr}</span>
                      </td>
                      <td className="py-4 px-6 text-sm font-medium text-zinc-400 hidden sm:table-cell">
                        {row.takeaway}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>

              {/* Detailed Report Toggle & Table */}
              <div className="border-t border-zinc-800">
                <button
                  onClick={() => setShowDetailed(!showDetailed)}
                  className="w-full py-3 px-6 flex items-center justify-between text-sm font-semibold text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200 transition-colors focus:outline-none"
                >
                  <span>{lang.detailedToggle}</span>
                  {showDetailed ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                </button>
                <AnimatePresence>
                  {showDetailed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="overflow-x-auto bg-zinc-900/50">
                        <table className="w-full text-left whitespace-nowrap">
                          <thead>
                            <tr className="border-t border-b border-zinc-800 bg-zinc-800/50">
                              <th className="py-3 px-6 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.useCase}</th>
                              <th className="py-3 px-4 text-xs font-bold text-blue-400 uppercase">{lang.detailedHeaders.fuseragDoora}</th>
                              <th className="py-3 px-4 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.graphrag}</th>
                              <th className="py-3 px-4 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.hipporag}</th>
                              <th className="py-3 px-4 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.adaptiverag}</th>
                              <th className="py-3 px-4 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.selfrag}</th>
                              <th className="py-3 px-4 text-xs font-bold text-zinc-400 uppercase">{lang.detailedHeaders.convrag}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lang.detailedReport.map((row, index) => (
                              <tr key={index} className="border-b border-zinc-800 last:border-0 hover:bg-zinc-950 transition-colors">
                                <td className="py-3 px-6 text-sm font-bold text-zinc-100">{row.useCase}</td>
                                <td className="py-3 px-4 text-sm font-bold text-blue-400">{row.fuserag}</td>
                                <td className="py-3 px-4 text-sm text-zinc-400">{row.graphrag}</td>
                                <td className="py-3 px-4 text-sm text-zinc-400">{row.hipporag}</td>
                                <td className="py-3 px-4 text-sm text-zinc-400">{row.adaptiverag}</td>
                                <td className="py-3 px-4 text-sm text-zinc-400">{row.selfrag}</td>
                                <td className="py-3 px-4 text-sm text-zinc-400">{row.convrag}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

            </div>
          </motion.div>

          {/* Research Quote */}
          <motion.div
            className="flex flex-col justify-center"
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-2xl p-8 text-white relative overflow-hidden h-full flex flex-col justify-center">
              {/* Background accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl -translate-y-16 translate-x-16" />

              <Target className="w-8 h-8 text-blue-300 mb-6" />
              <h3 className="text-xl font-bold mb-4 tracking-tight">{lang.research.title}</h3>
              <p className="text-sm text-blue-100/90 leading-relaxed font-medium mb-6 italic">
                {lang.research.quote}
              </p>
              <div className="mt-auto border-t border-white/10 pt-4">
                <p className="text-xs font-bold text-blue-300 uppercase tracking-widest">
                  {lang.research.readThePaper}
                </p>
                <a
                  href="/assets/FuseRAG__Parallel_Multi_Source_Knowledge_Fusion_for_Hybrid_Graph_Augmented_Retrieval_Augmented_Generation.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 mt-1 group"
                >
                  <span className="text-sm font-semibold text-white group-hover:text-blue-200 transition-colors underline decoration-blue-400/30 underline-offset-4">
                    {lang.research.paperTitle}
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Use Case Modal Context */}
      <AnimatePresence>
        {selectedUseCase && lang.useCaseSamples[selectedUseCase.key] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
            onClick={() => setSelectedUseCase(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="bg-zinc-950 rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl relative"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 z-10 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800 p-6 flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-bold text-zinc-100">{lang.modal.sampleTest}: {selectedUseCase.useCase}</h4>
                  <p className="text-xs text-zinc-400 mt-1 uppercase tracking-widest font-medium">{lang.modal.subtitle}</p>
                </div>
                <button
                  onClick={() => setSelectedUseCase(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-colors focus:outline-none"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Question */}
                <div className="bg-zinc-900 rounded-2xl p-5 border border-zinc-800">
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2">{lang.modal.userQuery}</div>
                  <p className="text-zinc-100 font-medium text-lg leading-snug">"{lang.useCaseSamples[selectedUseCase.key].question}"</p>
                </div>

                {/* Expected */}
                <div className="rounded-2xl p-5 border border-zinc-800 shadow-sm border-l-4 border-l-zinc-300">
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                    {lang.modal.expectedAnswer}
                  </div>
                  <p className="text-zinc-300 italic">
                    {lang.useCaseSamples[selectedUseCase.key].expected}
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Doora Answer */}
                  <div className="bg-blue-900/20 rounded-2xl p-5 border border-blue-800 flex flex-col h-full shadow-sm">
                    <div className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      {lang.modal.dooraOutput}
                    </div>
                    <p className="text-zinc-300 text-sm leading-relaxed flex-1">
                      {lang.useCaseSamples[selectedUseCase.key].doora}
                    </p>
                    <div className="mt-4 pt-3">
                      <span className="text-xs font-bold text-blue-400 bg-blue-900/30 px-2 py-1 rounded inline-flex">{lang.modal.highRelevanceClarity}</span>
                    </div>
                  </div>

                  {/* Competitor Answer */}
                  <div className="bg-red-50/10 rounded-2xl p-5 border border-red-100/50 flex flex-col h-full shadow-sm">
                    <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                      {lang.modal.averageAlternative}
                    </div>
                    <p className="text-zinc-400 text-sm leading-relaxed flex-1">
                      {lang.useCaseSamples[selectedUseCase.key].others}
                    </p>
                    <div className="mt-4 pt-3">
                      <span className="text-xs font-bold text-zinc-400 bg-zinc-800 px-2 py-1 rounded inline-flex">{lang.modal.lacksNuance}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default BenchmarkSection;
